/**
 * SkillRunModal — skill calistirma dialog'u
 * Parametre girisi + sonuc gosterimi
 */
import axios from 'axios'
import { Loader2, X, Zap } from 'lucide-react'
import { useState } from 'react'
import { API_URL } from '../../../../config/api'
import type { Skill } from '../hooks/useSkills'

interface Props {
  skill: Skill
  onClose: () => void
}

export default function SkillRunModal({ skill, onClose }: Props) {
  const [params, setParams] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [duration, setDuration] = useState<number | null>(null)

  const handleRun = async () => {
    setRunning(true)
    setResult(null)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) { setError('Not authenticated'); setRunning(false); return }

    let parsedParams: Record<string, unknown> = {}
    if (params.trim()) {
      try {
        parsedParams = JSON.parse(params) as Record<string, unknown>
      } catch {
        setError('Invalid JSON parameters')
        setRunning(false)
        return
      }
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/aicloud/skills/${skill.id}/run`,
        { parameters: parsedParams, agent_id: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const d = res.data as { result?: string; duration_seconds?: number; status?: string }
      if (d.status === 'failed') {
        setError(d.result ?? 'Execution failed')
      } else {
        setResult(d.result ?? '')
        setDuration(d.duration_seconds ?? null)
      }
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Connection failed')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--card-bg, #1c2128)',
          border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
          borderRadius: '14px',
          padding: '24px',
          width: '520px',
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: `${skill.color ?? '#3b82f6'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: skill.color ?? '#3b82f6' }}>
              <Zap size={18} />
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700 }}>Run: {skill.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary, #8b949e)' }}>{skill.category}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary, #8b949e)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Parameters */}
        {!result && (
          <>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary, #8b949e)', marginBottom: '6px' }}>
              Parameters (JSON, optional)
            </div>
            <textarea
              value={params}
              onChange={e => setParams(e.target.value)}
              placeholder={`{"query": "example", "max_results": 5}`}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px',
                background: 'var(--body-bg, #0d1117)',
                border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
                borderRadius: '8px', color: 'var(--text-primary, #e6edf3)',
                fontSize: '12px', fontFamily: 'monospace', resize: 'vertical',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            {skill.usage_example && (
              <div style={{ fontSize: '10px', color: 'var(--text-secondary, #8b949e)', marginTop: '4px', fontFamily: 'monospace' }}>
                Example: {skill.usage_example}
              </div>
            )}
          </>
        )}

        {/* Result */}
        {result !== null && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#10b981' }}>Result</div>
              {duration && <div style={{ fontSize: '10px', color: 'var(--text-secondary, #8b949e)' }}>{duration}s</div>}
            </div>
            <div style={{
              padding: '12px', background: 'var(--body-bg, #0d1117)',
              border: '1px solid var(--border-color, rgba(255,255,255,0.08))',
              borderRadius: '8px', fontSize: '12px', lineHeight: '1.6',
              maxHeight: '280px', overflowY: 'auto', whiteSpace: 'pre-wrap',
              color: 'var(--text-primary, #e6edf3)',
            }}>
              {result}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', fontSize: '12px', color: '#ef4444', marginTop: '8px' }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
          {result !== null && (
            <button
              onClick={() => { setResult(null); setError(null); setDuration(null) }}
              style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '8px', color: 'var(--text-secondary, #8b949e)', fontSize: '12px', cursor: 'pointer' }}
            >
              Run Again
            </button>
          )}
          <button onClick={onClose} style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--border-color, rgba(255,255,255,0.08))', borderRadius: '8px', color: 'var(--text-secondary, #8b949e)', fontSize: '12px', cursor: 'pointer' }}>
            Close
          </button>
          {result === null && (
            <button
              onClick={() => void handleRun()}
              disabled={running}
              style={{
                padding: '8px 16px', background: skill.color ?? '#3b82f6',
                color: 'white', border: 'none', borderRadius: '8px',
                fontSize: '12px', fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                opacity: running ? 0.7 : 1,
              }}
            >
              {running ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Running...</> : <><Zap size={13} /> Run Skill</>}
            </button>
          )}
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  )
}
